import React from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Typography} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';

interface Props {
    open: boolean;
    automationName: string;
    triggerName: string;
    hasUnsavedChanges: boolean;
    isRunning: boolean;
    disabledReason?: string;
    /** The trigger reminds now (utils/remindNow): the dialog says who gets a reminder. */
    remindNow?: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export default function AutomationRunDialog({open, automationName, triggerName, hasUnsavedChanges, isRunning, disabledReason, remindNow = false, onClose, onConfirm}: Props) {
    const {t} = useTranslation();
    const close = () => {if (!isRunning) onClose();};

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="automation-run-title" aria-describedby="automation-run-description">
            <DialogTitle id="automation-run-title" sx={{pb: 1}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="h6" component="span" fontWeight={600}>{t('Run for everyone')}</Typography>
                    <IconButton onClick={close} disabled={isRunning} size="small" aria-label={t('Close')}><CloseIcon/></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack gap={2}>
                    <DialogContentText id="automation-run-description" sx={{overflowWrap: 'anywhere'}}>
                        {remindNow
                            ? t('Remind now description', {name: automationName})
                            : t('Start "{{name}}" from "{{trigger}}" now. Emails go to their configured recipients.', {name: automationName, trigger: triggerName})}
                    </DialogContentText>
                    {hasUnsavedChanges && <Typography variant="body2" color="text.secondary">{t('Your changes will be saved before the run starts.')}</Typography>}
                    {disabledReason && <Alert severity="info">{disabledReason}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={isRunning}>{t('Cancel')}</Button>
                <LoadingButton variant="contained" loading={isRunning} disabled={!!disabledReason} onClick={onConfirm}>{t('Run for everyone')}</LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
